import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { currentLocale } from '../../i18n';
import MapImage from '../common/MapImage';
import './MapStats.css';

/**
 * Displays per-map statistics including play counts, round averages,
 * overtime/close game rates, and combat metrics (HS%, ADR, entry duels,
 * KAST, flash efficiency). Cards are sorted by most-played.
 */
export default function MapStats() {
  const { t } = useTranslation();
  const [mapStats, setMapStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/maps/stats`)
      .then(r => r.json())
      .then(d => { setMapStats(d.map_stats || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="map-stats-page"><div className="loading-screen"><div className="loader" /></div></div>;
  }

  const maxPlayed = Math.max(...mapStats.map(m => m.times_played), 1);

  return (
    <div className="map-stats-page">
      <h1 className="page-title">{t('maps.statsTitle')}</h1>

      <div className="map-stats-list">
        {mapStats.map(m => {
          const pct = Math.round((m.times_played / maxPlayed) * 100);
          const mapName = m.mapname || m.map_tag?.replace('de_', '') || '?';

          return (
            <div key={m.map_tag} className="mapcard">
              <div className="mapcard-image">
                <MapImage imageUrl={m.image_url} mapName={mapName} />
              </div>

              <div className="mapcard-body">
                <div className="mapcard-top">
                  <h3 className="mapcard-name">{mapName}</h3>
                  {m.last_played && (
                    <span className="mapcard-date">
                      {new Date(m.last_played).toLocaleDateString(currentLocale(), { day: 'numeric', month: 'short' })}
                    </span>
                  )}
                </div>

                <div className="mapcard-bar-wrap">
                  <div className="mapcard-bar" style={{ width: `${pct}%` }} />
                </div>

                <div className="mapcard-row">
                  <span className="mapcard-stat">
                    <strong>{m.times_played}</strong> {t('maps.matchesUnit')}
                  </span>
                  <span className="mapcard-stat">
                    <strong>{m.avg_total_rounds}</strong> {t('maps.avgRounds')}
                  </span>
                  {m.avg_duration_min > 0 && (
                    <span className="mapcard-stat">
                      <strong>{m.avg_duration_min}</strong> min
                    </span>
                  )}
                  {m.overtime_pct > 0 && (
                    <span className="mapcard-stat">
                      <strong>{m.overtime_pct}%</strong> OT
                    </span>
                  )}
                  {m.close_pct > 0 && (
                    <span className="mapcard-stat">
                      <strong>{m.close_pct}%</strong> {t('maps.closeGames')}
                    </span>
                  )}
                </div>

                <div className="mapcard-metrics">
                  {m.avg_hs_pct > 0 && (
                    <div className="mapcard-metric">
                      <span className="metric-val">{m.avg_hs_pct}%</span>
                      <span className="metric-lbl">HS</span>
                    </div>
                  )}
                  {m.avg_adr > 0 && (
                    <div className="mapcard-metric">
                      <span className="metric-val">{m.avg_adr}</span>
                      <span className="metric-lbl">ADR</span>
                    </div>
                  )}
                  {m.entry_win_pct > 0 && (
                    <div className="mapcard-metric">
                      <span className="metric-val">{m.entry_win_pct}%</span>
                      <span className="metric-lbl">Entry</span>
                    </div>
                  )}
                  {m.avg_kast > 0 && (
                    <div className="mapcard-metric">
                      <span className="metric-val">{m.avg_kast}%</span>
                      <span className="metric-lbl">KAST</span>
                    </div>
                  )}
                  {m.flash_eff > 0 && (
                    <div className="mapcard-metric">
                      <span className="metric-val">{m.flash_eff}%</span>
                      <span className="metric-lbl">Flash</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
